import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { errorText } from "@/lib/query/client";
import { qk } from "@/lib/query/keys";
import { Activity, Bot, ChevronRight, Cpu, Gauge, HardDrive, Loader2, MemoryStick, Server } from "lucide-react";
import { SignInFailureBanner } from "@/components/claude-account-settings";
import { RunStopButton } from "@/components/run-stop-button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Avatar, EmptyState, Skeleton } from "@/components/ui/misc";
import { Page, PageHeader } from "@/components/ui/page";
import { api } from "@/lib/api";
import { useCan } from "@/lib/auth";
import { APP_COMMIT, APP_VERSION } from "@/lib/app-version";
import { avatarOf } from "@/lib/avatars";
import { formatBytes as bytes, humanizeMs, relativeTime } from "@/lib/format";
import { modelLabel } from "@/lib/models";
import { previewText } from "@/lib/preview-text";
import type { StatusDashboard, UsageTotals, User } from "@/lib/types";
import { cn } from "@/lib/utils";

/** Refresh interval while the page is visible. The worker writes a host reading every 10s. */
const POLL_MS = 8_000;
const PERIODS = [
  { id: "today", label: "Today" },
  { id: "week", label: "This week" },
  { id: "month", label: "This month" },
] as const;
type Period = (typeof PERIODS)[number]["id"];
const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Claude and the server at a glance: what runs cost, how much of the plan's
 * windows is left, what is running and queued, and the host the worker runs on.
 * The first tab on a phone, a sidebar row on a desktop. Polls only while visible.
 */
const DESCRIPTION = "What Claude costs, how much of the plan is left, what is running and the host it runs on.";

export function StatusView({
  users,
  onOpenAgents,
  onOpenAgent,
  onOpenTask,
  showTitle = true,
}: {
  /** False with the tab layout, whose top bar already names the view. */
  showTitle?: boolean;
  /** For each agent's avatar beside its runs and its spend. */
  users: User[];
  onOpenAgents: () => void;
  onOpenAgent: (agentId: string) => void;
  onOpenTask: (taskId: string) => void;
}) {
  const [period, setPeriod] = useState<Period>("today");
  // Spending, host inventory and every agent's work: the server serves it to admins only.
  const isAdmin = useCan("admin");
  // Polled while the page is visible (react-query pauses an interval in a hidden tab),
  // and shown from the cache at once on a return.
  const query = useQuery({
    queryKey: qk.status(TIME_ZONE),
    queryFn: () => api.status(TIME_ZONE),
    enabled: isAdmin,
    refetchInterval: POLL_MS,
  });
  const data: StatusDashboard | null = query.data ?? null;
  const error = query.error ? errorText(query.error, "Could not load the status") : null;
  /** When the reading was taken, for "N s ago" — not the clock, which would drift from it. */
  const now = query.dataUpdatedAt || Date.now();

  if (!isAdmin) {
    return (
      <Page>
        <PageHeader title="Status" showTitle={showTitle} description={DESCRIPTION} />
        <EmptyState icon={<Gauge />} title="Admins only" hint="Status shows spending, the host and every agent's runs, so only an admin or the owner can see it." />
      </Page>
    );
  }

  if (!data) {
    return (
      <Page className="space-y-5">
        <PageHeader title="Status" showTitle={showTitle} description={DESCRIPTION} />
        {error ? (
          <EmptyState icon={<Gauge />} title="Could not load the status" hint={error} />
        ) : (
          <>
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-56 w-full" />
          </>
        )}
      </Page>
    );
  }

  const usage = data.usage[period];
  const { queued, running } = data.activity;
  const queuedTotal = queued.agentTurns + queued.responseTurns + queued.intakeMessages + queued.whatsappImports;

  return (
    <Page className="space-y-5">
      <PageHeader title="Status" showTitle={showTitle} description={DESCRIPTION} />
      {/* ------------------------------------------------------------ Claude */}
      <Section
        icon={<Bot />}
        title="Claude"
        aside={
          <div className="inline-flex rounded-lg border border-border/70 bg-muted/40 p-0.5" role="tablist">
            {PERIODS.map((option) => (
              <button
                key={option.id}
                role="tab"
                aria-selected={period === option.id}
                onClick={() => setPeriod(option.id)}
                className={cn(
                  "rounded-md px-2.5 py-1 text-[12px] transition-colors",
                  period === option.id ? "bg-background font-medium shadow-sm" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        }
      >
        <SignInFailureBanner failure={data.claudeAccounts?.signInFailure ?? null} />
        <div className="grid grid-cols-2 gap-3 win-lg:grid-cols-4 [&>*]:min-w-0">
          <Stat label="Cost" value={usd(usage.total.costUsd)} note={`${usage.total.runs} run${usage.total.runs === 1 ? "" : "s"}`} />
          <Stat label="Input" value={tokens(usage.total.inputTokens)} note="uncached tokens" />
          <Stat label="Output" value={tokens(usage.total.outputTokens)} note="tokens" />
          <Stat
            label="Cache"
            value={tokens(usage.total.cacheReadTokens + usage.total.cacheCreationTokens)}
            note={`${tokens(usage.total.cacheReadTokens)} read · ${tokens(usage.total.cacheCreationTokens)} written`}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 win-lg:grid-cols-2">
          <PlanLimits data={data} now={now} onOpenAgent={onOpenAgent} />

          <Card className="min-w-0 p-4">
            <CardHeading
              aside={
                <button onClick={onOpenAgents} className="inline-flex items-center gap-0.5 text-[12px] text-primary hover:underline">
                  Agents <ChevronRight className="size-3.5" />
                </button>
              }
            >
              Running now
            </CardHeading>
            {running.some((turn) => turn.lane === "parallel") ? (
              <p className="mb-1.5 text-[12px] text-muted-foreground">
                {running.length} of {data.activity.maxProcs} Claude processes (AGENT_MAX_PROCS)
              </p>
            ) : null}
            {running.length === 0 ? (
              <p className="text-[13px] text-muted-foreground">No agent is working.</p>
            ) : (
              <ul className="space-y-1.5">
                {running.map((turn) => (
                  <li key={turn.turnId} className="flex items-center gap-1">
                    <button
                      onClick={() => (turn.taskId ? onOpenTask(turn.taskId) : onOpenAgent(turn.agentId))}
                      className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1.5 py-1 text-left text-[13px] hover:bg-muted/60"
                      title={turn.branch ?? undefined}
                    >
                      <AvatarFor users={users} id={turn.agentId} name={turn.name} />
                      <span className="shrink-0 font-medium">{turn.name}</span>
                      {turn.lane === "parallel" ? (
                        <Badge variant="outline" className="shrink-0">
                          parallel
                        </Badge>
                      ) : null}
                      <span className="min-w-0 flex-1 truncate text-muted-foreground">{previewText(turn.summary)}</span>
                      {turn.claimedAt ? (
                        <span className="shrink-0 text-[11.5px] tabular-nums text-muted-foreground">
                          {humanizeMs(now - Date.parse(turn.claimedAt))}
                        </span>
                      ) : null}
                      <Loader2 className="size-3.5 shrink-0 animate-spin text-primary" />
                    </button>
                    <RunStopButton agentId={turn.agentId} turnId={turn.turnId} label={`${turn.name}'s run`} stopping={turn.stopping} />
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-border/60 pt-2.5 text-[12px] text-muted-foreground">
              <span>
                <b className="font-semibold tabular-nums text-foreground">{queuedTotal}</b> queued
              </span>
              {queued.agentTurns ? <span>{queued.agentTurns} agent turns</span> : null}
              {queued.responseTurns ? <span>{queued.responseTurns} replies</span> : null}
              {queued.intakeMessages ? <span>{queued.intakeMessages} pastes</span> : null}
              {queued.whatsappImports ? <span>{queued.whatsappImports} imports</span> : null}
            </div>
          </Card>
        </div>

        <DailyCost daily={data.daily} />

        <div className="grid grid-cols-1 gap-3 win-lg:grid-cols-2">
          <Breakdown
            title="By agent"
            rows={usage.byAgent.map((row) => ({
              key: row.agentId ?? "worker",
              name: row.name,
              avatar: row.agentId ? <AvatarFor users={users} id={row.agentId} name={row.name} /> : undefined,
              totals: row,
              onClick: row.agentId ? () => onOpenAgent(row.agentId!) : undefined,
            }))}
          />
          <Breakdown
            title="By project"
            rows={usage.byProject.map((row) => ({ key: row.projectId ?? "none", name: row.name, totals: row }))}
          />
          {/* Only once there is more than one account to tell apart. */}
          {(data.claudeAccounts?.accounts.length ?? 0) > 1 || usage.byAccount?.some((row) => row.account && row.account !== "default") ? (
            <Breakdown
              title="By Claude account"
              rows={(usage.byAccount ?? []).map((row) => ({
                key: row.account ?? "unrecorded",
                name: row.account ? accountLabel(row.account) : "Not recorded (before accounts)",
                totals: row,
              }))}
            />
          ) : null}
        </div>
        <p className="text-[11.5px] text-muted-foreground">
          Cost is what the CLI prices each run at list rates; on a subscription plan the limits above are what is spent.
          Periods start at midnight, Monday and the 1st in {data.timezone}.
        </p>
      </Section>

      {/* ------------------------------------------------------------ System */}
      <Section
        icon={<Server />}
        title="System"
        aside={
          data.host ? (
            <span className={cn("text-[12px]", data.host.stale ? "text-[var(--kind-blocked)]" : "text-muted-foreground")}>
              {data.host.stale ? "Stale — " : ""}
              {data.host.hostname} · {relativeTime(data.host.observedAt, now)}
            </span>
          ) : null
        }
      >
        {data.host ? (
          <>
            <div className="grid grid-cols-2 gap-3 win-lg:grid-cols-4 [&>*]:min-w-0">
              <Card className="p-4">
                <Meter
                  icon={<Cpu />}
                  label="CPU"
                  fraction={data.host.cpuPercent === null ? null : data.host.cpuPercent / 100}
                  detail={`load ${data.host.loadAverage.map((n) => n.toFixed(2)).join(" ")} · ${data.host.cpuCount} cores`}
                />
              </Card>
              <Card className="p-4">
                <Meter
                  icon={<MemoryStick />}
                  label="Memory"
                  fraction={data.host.memory.usedBytes / data.host.memory.totalBytes}
                  detail={`${bytes(data.host.memory.usedBytes)} of ${bytes(data.host.memory.totalBytes)}`}
                />
              </Card>
              <Card className="p-4">
                {data.host.disk ? (
                  <Meter
                    icon={<HardDrive />}
                    label={`Disk ${data.host.disk.path}`}
                    fraction={data.host.disk.usedBytes / data.host.disk.totalBytes}
                    detail={`${bytes(data.host.disk.usedBytes)} of ${bytes(data.host.disk.totalBytes)}`}
                  />
                ) : (
                  <p className="text-[13px] text-muted-foreground">Disk not readable</p>
                )}
              </Card>
              <Stat label="Uptime" value={humanizeMs(data.host.uptimeSeconds * 1000)} note="since the host booted" />
            </div>
            <Card className="divide-y divide-border/60">
              {data.host.worker ? (
                <ServiceRow
                  name={data.host.worker.name}
                  kind="pm2"
                  ok={data.host.worker.status === "online"}
                  state={data.host.worker.status}
                  detail={[
                    data.host.worker.uptimeSeconds !== null ? `up ${humanizeMs(data.host.worker.uptimeSeconds * 1000)}` : null,
                    `${data.host.worker.restarts} restarts`,
                    data.host.worker.memoryBytes ? bytes(data.host.worker.memoryBytes) : null,
                  ]}
                />
              ) : null}
              {data.host.containers.map((container) => (
                <ServiceRow
                  key={container.name}
                  name={container.name}
                  kind="docker"
                  ok={container.state === "running"}
                  state={container.state}
                  detail={[
                    container.status,
                    container.restartCount !== null ? `${container.restartCount} restarts` : null,
                  ]}
                />
              ))}
            </Card>
          </>
        ) : (
          <Card className="p-4 text-[13px] text-muted-foreground">
            No reading from the worker yet. It writes one every 10 seconds while it runs on the host; if this stays empty,
            the worker is not running (or runs with <code>STATUS_HOST=false</code>).
          </Card>
        )}
        <p className="text-[12.5px] text-muted-foreground">
          Board <span className="font-medium text-foreground">{APP_VERSION}</span>
          {APP_COMMIT ? (
            <>
              {" "}
              · deployed <code className="font-mono text-[12px]">{APP_COMMIT}</code>
            </>
          ) : null}{" "}
          · what changed is under More → Changelog.
        </p>
      </Section>
    </Page>
  );
}

const accountLabel = (name: string) => (name === "default" ? "Default login" : name);

/**
 * Plan limits, per Claude account. Each account has its own plan, so this follows
 * the account in use — a switch in Settings moves it — and with several a row of
 * chips shows any other. A reading from the usage API (what `/usage` shows, Fable's
 * weekly limit included) wins; without one (a token that cannot read usage, or none
 * yet) it is the windows the account's last run streamed.
 */
function PlanLimits({ data, now, onOpenAgent }: { data: StatusDashboard; now: number; onOpenAgent: (agentId: string) => void }) {
  const accounts = data.claudeAccounts?.accounts ?? [];
  const [picked, setPicked] = useState<string | null>(null);
  const active = accounts.find((account) => account.active) ?? accounts[0] ?? null;
  const shown = accounts.find((account) => account.name === picked) ?? active;
  const plan = shown?.plan && shown.plan.limits.length > 0 ? shown.plan : null;
  // A server from before per-account readings: the one row it had.
  const lastRun = shown ? shown.lastRun : data.rateLimit;
  const windows = lastRun && Object.keys(lastRun.windows).length > 0 ? lastRun : null;
  const resets = (at: string | null) => (at ? ` · resets in ${humanizeMs(Date.parse(at) - now)}` : "");
  return (
    <Card className="min-w-0 p-4">
      <CardHeading
        aside={
          shown && accounts.length === 1 && shown.name !== "default" ? (
            <span className="text-[12px] text-muted-foreground">{accountLabel(shown.name)}</span>
          ) : null
        }
      >
        Plan limits
      </CardHeading>
      {accounts.length > 1 ? (
        <div className="-mt-1 mb-3 flex flex-wrap gap-1.5" role="tablist" aria-label="Claude account">
          {accounts.map((account) => (
            <button
              key={account.name}
              role="tab"
              aria-selected={account.name === shown?.name}
              onClick={() => setPicked(account.active ? null : account.name)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[12px] transition-colors",
                account.name === shown?.name
                  ? "border-ring/60 bg-accent font-medium text-foreground"
                  : "border-border/70 text-muted-foreground hover:bg-muted/50 hover:text-foreground",
              )}
            >
              {account.active ? <span aria-hidden className="size-1.5 rounded-full bg-[var(--kind-done)]" /> : null}
              {accountLabel(account.name)}
              {account.active ? <span className="text-[11px] text-muted-foreground">in use</span> : null}
            </button>
          ))}
        </div>
      ) : null}
      {plan ? (
        <div className="space-y-3">
          {plan.limits.map((limit) => (
            <Meter
              key={`${limit.kind}:${limit.model ?? ""}:${limit.label}`}
              label={limit.label}
              fraction={limit.utilization}
              detail={limit.utilization === null ? "not reported" : `${Math.round((1 - limit.utilization) * 100)}% left${resets(limit.resetsAt)}`}
            />
          ))}
          <p className="text-[11.5px] text-muted-foreground">
            {windows ? (windows.status === "allowed" ? "Within limits" : `Status: ${windows.status ?? "unknown"}`) : "Plan usage"}
            {windows?.isUsingOverage ? " · using overage" : ""} · from Anthropic's usage API{" "}
            {plan.observedAt ? relativeTime(plan.observedAt, now) : ""}
          </p>
        </div>
      ) : windows ? (
        <div className="space-y-3">
          {Object.entries(windows.windows).map(([name, window]) => (
            <Meter
              key={name}
              label={WINDOW_LABELS[name] ?? name.replace(/_/g, " ")}
              fraction={window.utilization}
              detail={window.utilization === null ? "not reported" : `${Math.round((1 - window.utilization) * 100)}% left${resets(window.resetsAt)}`}
            />
          ))}
          <p className="text-[11.5px] text-muted-foreground">
            {windows.status === "allowed" ? "Within limits" : `Status: ${windows.status ?? "unknown"}`}
            {windows.isUsingOverage ? " · using overage" : ""} · as of this account's last agent run, {relativeTime(windows.observedAt, now)}
            {shown?.plan?.error ? ` · model limits unavailable: ${shown.plan.error}` : ""}
          </p>
        </div>
      ) : (
        <p className="text-[13px] text-muted-foreground">
          {shown?.plan?.error
            ? `No reading for ${accountLabel(shown.name)} yet: ${shown.plan.error}.`
            : "No reading yet. The worker reads each account's limits every few minutes, and every agent run reports its windows."}
        </p>
      )}
      <FallbackList data={data} now={now} onOpenAgent={onOpenAgent} />
    </Card>
  );
}

const WINDOW_LABELS: Record<string, string> = {
  five_hour: "5-hour window",
  seven_day: "7-day window",
  seven_day_opus: "7-day window (Opus)",
  seven_day_sonnet: "7-day window (Sonnet)",
};

const usd = (value: number) => (value >= 100 ? `$${value.toFixed(0)}` : value >= 1 ? `$${value.toFixed(2)}` : `$${value.toFixed(3)}`);

/** Agents whose model is at its usage limit, each running on its fallback until the reset. */
function FallbackList({ data, now, onOpenAgent }: { data: StatusDashboard; now: number; onOpenAgent: (agentId: string) => void }) {
  const rows = (data.onFallback ?? []).filter((row) => Date.parse(row.until) > now);
  if (rows.length === 0) return null;
  return (
    <ul className="mt-3 space-y-1 border-t border-border/60 pt-2.5 text-[12px]">
      {rows.map((row) => (
        <li key={row.agentId}>
          <button onClick={() => onOpenAgent(row.agentId)} className="flex w-full items-center gap-1.5 text-left hover:underline">
            <Gauge className="size-3.5 shrink-0" style={{ color: "var(--prio-high)" }} />
            <span className="font-medium">{row.name}</span>
            <span className="min-w-0 truncate text-muted-foreground">
              on {modelLabel(row.fallbackModel)} ({row.model ? modelLabel(row.model) : "model"} limit) · back in {humanizeMs(Date.parse(row.until) - now)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function tokens(value: number): string {
  if (value >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(1)}k`;
  return String(value);
}

/** Status from how full something is, always shown with its words, never colour alone. */
function level(fraction: number): { color: string; word: string } {
  if (fraction >= 0.9) return { color: "var(--kind-blocked)", word: "critical" };
  if (fraction >= 0.75) return { color: "var(--kind-review)", word: "high" };
  return { color: "var(--kind-done)", word: "ok" };
}

const Section = ({
  icon,
  title,
  aside,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <section className="space-y-3">
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-muted-foreground [&_svg]:size-4">{icon}</span>
      <h2 className="flex-1 text-[15px] font-semibold tracking-tight">{title}</h2>
      {aside}
    </div>
    {children}
  </section>
);

const CardHeading = ({ children, aside }: { children: React.ReactNode; aside?: React.ReactNode }) => (
  <div className="mb-3 flex items-center">
    <h3 className="flex-1 text-[13px] font-semibold">{children}</h3>
    {aside}
  </div>
);

const Stat = ({ label, value, note }: { label: string; value: string; note?: string }) => (
  <Card className="min-w-0 p-4">
    <p className="text-[12px] text-muted-foreground">{label}</p>
    <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">{value}</p>
    {note ? <p className="mt-0.5 truncate text-[11.5px] text-muted-foreground">{note}</p> : null}
  </Card>
);

const Meter = ({
  icon,
  label,
  fraction,
  detail,
}: {
  icon?: React.ReactNode;
  label: string;
  fraction: number | null;
  detail: string;
}) => {
  const status = fraction === null ? null : level(fraction);
  const percent = fraction === null ? null : Math.round(Math.min(1, Math.max(0, fraction)) * 100);
  return (
    <div>
      <div className="mb-1.5 flex items-baseline gap-1.5">
        {icon ? <span className="self-center text-muted-foreground [&_svg]:size-3.5">{icon}</span> : null}
        <span className="flex-1 truncate text-[12.5px] font-medium">{label}</span>
        <span className="text-[15px] font-semibold tabular-nums">{percent === null ? "—" : `${percent}%`}</span>
        {status && status.word !== "ok" ? (
          <span className="text-[11px] font-medium" style={{ color: status.color }}>
            {status.word}
          </span>
        ) : null}
      </div>
      <div
        className="h-1.5 overflow-hidden rounded-full bg-muted"
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent ?? undefined}
      >
        {percent !== null ? (
          <div className="h-full rounded-full transition-[width]" style={{ width: `${percent}%`, backgroundColor: status!.color }} />
        ) : null}
      </div>
      <p className="mt-1.5 truncate text-[11.5px] text-muted-foreground">{detail}</p>
    </div>
  );
};

/** The last fortnight's cost, one bar a day; days without runs are drawn empty. */
function DailyCost({ daily }: { daily: StatusDashboard["daily"] }) {
  const byDay = new Map(daily.map((entry) => [entry.day, entry]));
  const days: Array<{ day: string; costUsd: number; runs: number; tokens: number }> = [];
  for (let i = 13; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    days.push(byDay.get(key) ?? { day: key, costUsd: 0, runs: 0, tokens: 0 });
  }
  const max = Math.max(...days.map((d) => d.costUsd), 0.0001);
  const total = days.reduce((sum, d) => sum + d.costUsd, 0);
  return (
    <Card className="p-4">
      <CardHeading aside={<span className="text-[12px] tabular-nums text-muted-foreground">{usd(total)} in 14 days</span>}>
        Cost per day
      </CardHeading>
      <div className="flex h-28 items-end gap-[2px]">
        {days.map((d) => (
          <div
            key={d.day}
            className="group relative flex h-full flex-1 flex-col justify-end"
            title={`${d.day}: ${usd(d.costUsd)} · ${d.runs} runs · ${tokens(d.tokens)} tokens`}
          >
            <div
              className="rounded-t-[4px] bg-primary/80 transition-colors group-hover:bg-primary"
              style={{ height: d.costUsd > 0 ? `${Math.max(3, (d.costUsd / max) * 100)}%` : 0 }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-[10.5px] text-muted-foreground">
        <span>{shortDay(days[0]!.day)}</span>
        <span>Today</span>
      </div>
    </Card>
  );
}

const shortDay = (key: string) =>
  new Date(`${key}T12:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });

/** The avatar of the user `id`, or initials of `name` when the list does not have them. */
const AvatarFor = ({ users, id, name }: { users: User[]; id: string; name: string }) => {
  const user = users.find((candidate) => candidate.id === id);
  return <Avatar {...(user ? avatarOf(user) : { name })} size="sm" />;
};

function Breakdown({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ key: string; name: string; avatar?: React.ReactNode; totals: UsageTotals; onClick?: () => void }>;
}) {
  const max = Math.max(...rows.map((row) => row.totals.costUsd), 0.0001);
  return (
    <Card className="min-w-0 p-4">
      <CardHeading>{title}</CardHeading>
      {rows.length === 0 ? (
        <p className="text-[13px] text-muted-foreground">No runs in this period.</p>
      ) : (
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-[11px] text-muted-foreground">
              <th className="pb-1.5 font-normal">Name</th>
              <th className="pb-1.5 text-right font-normal">Runs</th>
              <th className="pb-1.5 text-right font-normal">Tokens</th>
              <th className="pb-1.5 text-right font-normal">Cost</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const all =
                row.totals.inputTokens + row.totals.outputTokens + row.totals.cacheReadTokens + row.totals.cacheCreationTokens;
              return (
                <tr key={row.key} className="border-t border-border/50">
                  <td className="max-w-0 py-1.5 pr-2">
                    <div className="flex min-w-0 items-center gap-1.5">
                      {row.avatar}
                      {row.onClick ? (
                        <button onClick={row.onClick} className="block min-w-0 flex-1 truncate text-left font-medium hover:underline">
                          {row.name}
                        </button>
                      ) : (
                        <span className="block min-w-0 flex-1 truncate font-medium">{row.name}</span>
                      )}
                    </div>
                    <div className="mt-1 h-1 rounded-full bg-muted">
                      <div className="h-full rounded-full bg-primary/70" style={{ width: `${(row.totals.costUsd / max) * 100}%` }} />
                    </div>
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-muted-foreground">{row.totals.runs}</td>
                  <td
                    className="py-1.5 text-right tabular-nums text-muted-foreground"
                    title={`in ${tokens(row.totals.inputTokens)} · out ${tokens(row.totals.outputTokens)} · cache ${tokens(row.totals.cacheReadTokens + row.totals.cacheCreationTokens)}`}
                  >
                    {tokens(all)}
                  </td>
                  <td className="py-1.5 text-right font-medium tabular-nums">{usd(row.totals.costUsd)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function ServiceRow({
  name,
  kind,
  ok,
  state,
  detail,
}: {
  name: string;
  kind: string;
  ok: boolean;
  state: string;
  detail: Array<string | null>;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
      <Activity className="size-3.5 shrink-0" style={{ color: ok ? "var(--kind-done)" : "var(--kind-blocked)" }} />
      <span className="font-medium">{name}</span>
      <span className="text-[11px] text-muted-foreground">{kind}</span>
      <span className="min-w-0 flex-1 truncate text-right text-[12px] text-muted-foreground">
        {detail.filter(Boolean).join(" · ")}
      </span>
      <span className="shrink-0 text-[12px] font-medium" style={{ color: ok ? "var(--kind-done)" : "var(--kind-blocked)" }}>
        {state}
      </span>
    </div>
  );
}
